import { getAccessToken } from '../lib/authSession';
/**
 * Offline Sync Manager
 * Handles synchronization of offline changes with the server
 */

import { offlineStorage } from './offlineStorage';
import { conflictResolver } from './conflictResolver';

const API_URL = process.env.REACT_APP_BACKEND_URL;

class SyncManager {
  constructor() {
    this.isOnline = navigator.onLine;
    this.isSyncing = false;
    this.listeners = new Set();
    
    // Listen for online/offline events
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());
  }

  // ==================== EVENT HANDLERS ====================

  handleOnline() {
    this.isOnline = true;
    this.notifyListeners({ type: 'online' });
    this.syncPendingChanges();
  }

  handleOffline() {
    this.isOnline = false;
    this.notifyListeners({ type: 'offline' });
  }

  // ==================== LISTENERS ====================

  addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  notifyListeners(event) {
    this.listeners.forEach(callback => {
      try {
        callback(event);
      } catch (e) {
        console.error('Sync listener error:', e);
      }
    });
  }

  // ==================== FETCH WITH OFFLINE SUPPORT ====================

  async fetchWithOfflineSupport(url, options = {}) {
    const token = getAccessToken();
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers
    };

    if (this.isOnline) {
      try {
        const response = await fetch(url, { ...options, headers });
        return response;
      } catch (error) {
        console.warn('Fetch failed, might be offline:', error);
        this.isOnline = false;
        this.notifyListeners({ type: 'offline' });
        throw error;
      }
    } else {
      throw new Error('offline');
    }
  }

  // ==================== CONTRACT OPERATIONS ====================

  async fetchContracts() {
    try {
      const response = await this.fetchWithOfflineSupport(`${API_URL}/api/contracts`);
      if (response.ok) {
        const contracts = await response.json();
        // Cache all contracts for offline use
        await offlineStorage.saveContracts(contracts);
        await offlineStorage.setMetadata('last_sync', new Date().toISOString());
        this.notifyListeners({ type: 'sync_complete', count: contracts.length });
        return { source: 'server', data: contracts };
      }
    } catch (error) {
      if (error.message === 'offline' || !this.isOnline) {
        // Return cached contracts
        const cached = await offlineStorage.getAllContracts();
        return { source: 'cache', data: cached };
      }
      throw error;
    }
  }

  async fetchContract(id) {
    try {
      const response = await this.fetchWithOfflineSupport(`${API_URL}/api/contracts/${id}`);
      if (response.ok) {
        const contract = await response.json();
        // Cache for offline use
        await offlineStorage.saveContract(contract);
        return { source: 'server', data: contract };
      }
    } catch (error) {
      if (error.message === 'offline' || !this.isOnline) {
        // Return cached contract
        const cached = await offlineStorage.getContract(id);
        if (cached) {
          return { source: 'cache', data: cached };
        }
        throw new Error('Contract not available offline');
      }
      throw error;
    }
  }

  async saveContractOffline(contractId, content, title) {
    // Save the change locally
    await offlineStorage.savePendingChange(contractId, 'edit', {
      content,
      title,
      edited_at: new Date().toISOString()
    });

    // Update local contract copy
    const contract = await offlineStorage.getContract(contractId);
    if (contract) {
      contract.content = content;
      if (title) contract.title = title;
      contract.sync_status = 'pending';
      contract._local_edit_at = new Date().toISOString();
      await offlineStorage.saveContract(contract);
    }

    // Add to sync queue
    await offlineStorage.addToSyncQueue('update_contract', {
      contract_id: contractId,
      content,
      title
    }, 2);

    this.notifyListeners({ 
      type: 'change_saved', 
      contractId,
      pendingSync: true 
    });

    // Try to sync immediately if online
    if (this.isOnline) {
      this.syncPendingChanges();
    }

    return true;
  }

  // ==================== SYNC OPERATIONS ====================

  async syncPendingChanges() {
    if (this.isSyncing || !this.isOnline) return;
    
    this.isSyncing = true;
    this.notifyListeners({ type: 'sync_started' });

    try {
      const queue = await offlineStorage.getSyncQueue();
      let synced = 0;
      let failed = 0;

      for (const item of queue) {
        try {
          await this.processSyncItem(item);
          await offlineStorage.removeFromSyncQueue(item.queue_id);
          synced++;
        } catch (error) {
          console.error('Sync item failed:', error);
          failed++;
          
          // Update attempt count
          await offlineStorage.updateSyncQueueItem(item.queue_id, {
            attempts: (item.attempts || 0) + 1,
            last_attempt: new Date().toISOString(),
            last_error: error.message
          });
        }
      }

      // Clear synced pending changes
      const pendingChanges = await offlineStorage.getPendingChanges();
      for (const change of pendingChanges) {
        if (change.synced) {
          await offlineStorage.markChangeAsSynced(change.change_id);
        }
      }

      // Update last sync time
      await offlineStorage.setMetadata('last_sync', new Date().toISOString());

      this.notifyListeners({ 
        type: 'sync_complete', 
        synced, 
        failed,
        remaining: failed
      });

    } catch (error) {
      console.error('Sync failed:', error);
      this.notifyListeners({ type: 'sync_error', error: error.message });
    } finally {
      this.isSyncing = false;
    }
  }

  async processSyncItem(item) {
    const token = getAccessToken();
    
    switch (item.operation) {
      case 'update_contract':
        // First, fetch current server version to check for conflicts
        const serverResponse = await fetch(`${API_URL}/api/contracts/${item.data.contract_id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (serverResponse.ok) {
          const serverContract = await serverResponse.json();
          const localContract = await offlineStorage.getContract(item.data.contract_id);
          
          // Check for conflicts
          const conflicts = conflictResolver.detectConflicts(localContract, serverContract);
          
          if (conflicts.length > 0) {
            // Register conflict for manual resolution
            conflictResolver.registerConflict(item.data.contract_id, conflicts);
            this.notifyListeners({
              type: 'conflict_detected',
              contractId: item.data.contract_id,
              conflicts
            });
            throw new Error('Conflict detected - manual resolution required');
          }
        }
        
        // No conflicts, proceed with update
        const response = await fetch(`${API_URL}/api/contracts/${item.data.contract_id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            content: item.data.content,
            title: item.data.title
          })
        });
        
        if (!response.ok) {
          throw new Error(`Failed to sync: ${response.status}`);
        }
        
        // Update local contract status
        const contract = await offlineStorage.getContract(item.data.contract_id);
        if (contract) {
          contract.sync_status = 'synced';
          contract._has_pending_changes = false;
          await offlineStorage.saveContract(contract);
        }
        
        // Clear pending changes for this contract
        await offlineStorage.clearPendingChanges(item.data.contract_id);
        break;

      case 'create_contract':
        // Handle new contract creation
        break;

      default:
        console.warn('Unknown sync operation:', item.operation);
    }
  }

  // ==================== UTILITY ====================

  async getStatus() {
    const stats = await offlineStorage.getStorageStats();
    return {
      isOnline: this.isOnline,
      isSyncing: this.isSyncing,
      ...stats
    };
  }

  async forcSync() {
    if (this.isOnline) {
      await this.syncPendingChanges();
    }
  }
}

// Export singleton instance
export const syncManager = new SyncManager();
export default syncManager;
